import * as React from 'react';
import {getDoc} from 'firebase/firestore';
import {useAuth} from '../auth/AuthContext';
import {playlistDoc} from '../firebase/firestore';
import {UserPlaylist} from '../store/slices/userPlaylists/reducers';
import {useUserPlaylistById} from '../store/slices/userPlaylists/userPlaylistsSlice';

export interface SharedPlaylistResult {
    playlist: UserPlaylist | undefined;
    isOwner: boolean;
    loading: boolean;
    notFound: boolean;
}

/**
 * Returns a user playlist whether it's owned locally or shared by another user.
 *
 * - If the user owns it, it's read from Redux (live via FirestorePlaylistSync).
 * - Otherwise we fetch once from Firestore; security rules allow the read only if
 *   the doc is public (or the caller is the owner — the local-first path covers that).
 */
export const useSharedPlaylist = (id: string | undefined): SharedPlaylistResult => {
    const local = useUserPlaylistById(id ?? '');
    const {user} = useAuth();

    const [remote, setRemote] = React.useState<UserPlaylist | undefined>(undefined);
    const [loading, setLoading] = React.useState(false);
    const [notFound, setNotFound] = React.useState(false);

    React.useEffect(() => {
        if (!id || !id.startsWith('user-') || local) {
            setRemote(undefined);
            setNotFound(false);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setNotFound(false);

        getDoc(playlistDoc(id))
            .then(snap => {
                if (cancelled) return;
                if (snap.exists()) {
                    setRemote(snap.data() as UserPlaylist);
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => {
                if (!cancelled) setNotFound(true); // permission-denied → treat as not found from the user's POV
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [id, local]);

    const playlist = local ?? remote;
    const isOwner = !!(playlist && user && playlist.ownerId === user.uid);
    return {playlist, isOwner, loading, notFound};
};
